import { useCallback, useMemo } from 'react';

import useLabels from '~hooks/useLabels';
import { Filter } from '~modules/media-library/model/Filter';
import { getFilterOptionFromListById } from '~modules/media-library/model/FilterHelper';
import { FilterOption } from '~modules/media-library/model/FilterOption';
import { Button } from '~modules/media-library/view/Button';
import { Tag } from '~modules/media-library/view/TagBar/Tag';

type TagBarProps = {
  hideOnEmpty?: boolean;
  filterList: Filter[];
  selectedFilterValueIds: Array<FilterOption['id']>;
  onChangeSelectedFilterValueIds: (valueIds: Array<FilterOption['id']>) => void;
};

export function TagBar({
  hideOnEmpty = true,
  filterList,
  selectedFilterValueIds = [],
  onChangeSelectedFilterValueIds,
}: TagBarProps) {
  // Labels
  const { btnMediaLibraryResetTags, labelMediaLibraryTags } = useLabels([
    'btnMediaLibraryResetTags',
    'labelMediaLibraryTags',
  ]);

  // Handler: on clear of one filter remove all matching selected values from the array and pass it up
  const makeHandleTagOnClear = useCallback(
    (id: FilterOption['id']) => () => {
      onChangeSelectedFilterValueIds(selectedFilterValueIds.filter(valueId => valueId !== id));
    },
    [onChangeSelectedFilterValueIds, selectedFilterValueIds],
  );

  // Handler: on clear all filter values
  const handleOnClear = useCallback(() => {
    onChangeSelectedFilterValueIds([]);
  }, [onChangeSelectedFilterValueIds]);

  // Derived: calc list of filters to show to the user
  const activeFilterOptionList = useMemo(
    () =>
      (selectedFilterValueIds || []).map(filterId => getFilterOptionFromListById(filterList, filterId)).filter(Boolean),
    [filterList, selectedFilterValueIds],
  );

  // Early return when no filters are selected
  if (hideOnEmpty && selectedFilterValueIds.length === 0) return null;

  return (
    <div className="ml--tagbar w-full flex items-baseline gap-2">
      <div className="ml--tagbar-label font-sans text-sm text-darkgray select-none whitespace-nowrap">
        {labelMediaLibraryTags}
      </div>
      <div className="ml--tagbar-list flex flex-wrap gap-2">
        {activeFilterOptionList.map(filterOption => {
          return (
            <Tag key={filterOption.id} label={filterOption.label} onClear={makeHandleTagOnClear(filterOption.id)} />
          );
        })}
      </div>
      <div className="ml--tagbar-action">
        <Button variant="link" className="font-semibold" onClick={handleOnClear}>
          {btnMediaLibraryResetTags}
        </Button>
      </div>
    </div>
  );
}
